import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
} from 'recharts';
import {
    colors,
    lineGraphDotSize,
    lineGridStyle,
} from '../../../../shared/constants/constants';
import CustomTooltip from '../../CustomTooltip';
import {
    getXAxisLabel,
    getYAxisLabel,
    getYAxisTickFormatter,
    getXAxisTickFormatter,
    getYAxisDataKey,
    getChartMargin,
} from '../../../dashboardUtils';

// eslint-disable-next-line complexity
function GaugeComposedBarLineGraph({
    barDataLabels,
    chartProperties,
    dataFormatter,
    dataPoints,
    firstGroupKey,
    groupAxisFormatter,
    lineDataLabels,
    legendFormatter,
    thresholds,
    xAxisLabel,
    yAxisLabelLeft,
}) {
    const [activeIndex, setActiveIndex] = useState(-1);
    const {
        barProperties,
        colorPalette = colors,
        composedChartProperties,
        legendProperties,
        lineProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        showLegend,
        showSinglePointTooltip,
        suffix = '',
        timeFormat,
        tooltipProperties,
        xAxisProperties,
        yAxisProperties,
    } = chartProperties;

    const isVertical = false;
    const xAxisTickFormatter = getXAxisTickFormatter(
        isVertical,
        groupAxisFormatter,
        dataFormatter,
        timeFormat,
        suffix
    );

    const yAxisTickFormatter = getYAxisTickFormatter(
        isVertical,
        groupAxisFormatter,
        dataFormatter,
        timeFormat,
        suffix
    );

    const chartMargin = getChartMargin(
        dataPoints,
        firstGroupKey,
        suffix,
        thresholds,
        timeFormat,
        yAxisTickFormatter,
        yAxisLabelLeft,
        isVertical
    );

    return (
        <ResponsiveContainer {...responsiveContainerProperties}>
            <ComposedChart {...composedChartProperties} data={dataPoints}>
                <CartesianGrid strokeDasharray={lineGridStyle} />
                <XAxis
                    {...xAxisProperties}
                    dataKey={firstGroupKey}
                    tickFormatter={xAxisTickFormatter}
                    label={getXAxisLabel(xAxisLabel, xAxisProperties)}
                />
                <YAxis
                    {...yAxisProperties}
                    dataKey={getYAxisDataKey(isVertical, firstGroupKey)}
                    label={getYAxisLabel(yAxisLabelLeft)}
                    tickFormatter={yAxisTickFormatter}
                    width={chartMargin.left}
                />
                <Tooltip
                    {...tooltipProperties}
                    cursor={dataPoints.length !== 0}
                    content={
                        <CustomTooltip
                            activeIndex={activeIndex}
                            axisFormatter={
                                isVertical
                                    ? yAxisTickFormatter
                                    : xAxisTickFormatter
                            }
                            axisKey={firstGroupKey}
                            dataFormat={timeFormat}
                            dataFormatter={dataFormatter}
                            legendFormatter={legendFormatter}
                            showSinglePoint={showSinglePointTooltip}
                            suffix={suffix}
                            thresholds={thresholds}
                        />
                    }
                />
                {showLegend && (
                    <Legend {...legendProperties} formatter={legendFormatter} />
                )}
                {barDataLabels.map((barLabel, index) => (
                    <Bar
                        {...barProperties}
                        key={barLabel}
                        dataKey={barLabel}
                        fill={colorPalette[index % colorPalette.length]}
                        onMouseOver={() => setActiveIndex(index)}
                        onMouseOut={() => setActiveIndex(-1)}
                        onFocus={() => setActiveIndex(index)}
                        onBlur={() => setActiveIndex(-1)}
                    />
                ))}
                {lineDataLabels.map((lineLabel, index) => (
                    <Line
                        {...lineProperties}
                        key={lineLabel}
                        dataKey={lineLabel}
                        stroke={
                            colorPalette[
                                (barDataLabels.length + index) %
                                    colorPalette.length
                            ]
                        }
                        activeDot={{
                            r: lineGraphDotSize,
                            onMouseOver: () =>
                                setActiveIndex(barDataLabels.length + index),
                            onMouseOut: () => setActiveIndex(-1),
                            onFocus: () =>
                                setActiveIndex(barDataLabels.length + index),
                            onBlur: () => setActiveIndex(-1),
                        }}
                    />
                ))}
                {thresholds.map((threshold) => (
                    <ReferenceLine
                        key={`threshold-${threshold.id}`}
                        {...referenceLineProperties}
                        x={isVertical ? threshold.value : null}
                        y={isVertical ? null : threshold.value}
                        stroke={threshold.lineColor}
                    />
                ))}
            </ComposedChart>
        </ResponsiveContainer>
    );
}

GaugeComposedBarLineGraph.propTypes = {
    barDataLabels: PropTypes.instanceOf(Array),
    chartProperties: PropTypes.instanceOf(Object).isRequired,
    dataFormatter: PropTypes.func.isRequired,
    dataPoints: PropTypes.instanceOf(Array),
    firstGroupKey: PropTypes.string.isRequired,
    groupAxisFormatter: PropTypes.func.isRequired,
    legendFormatter: PropTypes.func.isRequired,
    lineDataLabels: PropTypes.instanceOf(Array),
    thresholds: PropTypes.instanceOf(Array).isRequired,
    xAxisLabel: PropTypes.string,
    yAxisLabelLeft: PropTypes.string,
};

GaugeComposedBarLineGraph.defaultProps = {
    dataPoints: [],
    barDataLabels: [],
    lineDataLabels: [],
    xAxisLabel: null,
    yAxisLabelLeft: null,
};

export default React.memo(
    GaugeComposedBarLineGraph,
    (prevProps, nextProps) =>
        _.isEqual(prevProps.dataPoints, nextProps.dataPoints) &&
        _.isEqual(prevProps.thresholds, nextProps.thresholds)
);
